import React from 'react';
import { Link } from 'react-router-dom';

export function Dashboard() {
  return (
    <div>
      <h1 className="text-3xl font-semibold tracking-tight text-ink-900">Dashboard</h1>
      <p className="mt-1.5 text-ink-600">Run defensible HOA votes from one place.</p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card href="/app/elections" title="Elections" body="Create, launch, and track HOA votes." icon={VoteIcon} />
        <Card href="/app/residents" title="Residents" body="Manage your roster and voter eligibility." icon={UsersIcon} />
        <Card href="/app/lots" title="Lots" body="Lot records, voting interest, co-owner links." icon={HomeIcon} />
        <Card href="/app/meetings" title="Meetings" body="Schedule meetings and sign minutes." icon={CalendarIcon} />
        <Card href="/app/documents" title="Documents" body="Host bylaws, rules, and budgets." icon={FileIcon} />
        <Card href="/app/settings" title="Settings" body="Branding, email provider, and billing." icon={SettingsIcon} />
      </div>

      <div className="mt-12 p-5 rounded-xl bg-white border border-ink-200">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand-600 grid place-items-center flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
          </div>
          <div>
            <div className="font-semibold text-ink-900">Verify your audit chain anytime</div>
            <p className="mt-1 text-sm text-ink-600">
              Your HOA's audit chain is publicly verifiable at{' '}
              <Link to="/verify" className="text-brand-700 font-medium hover:underline">/verify</Link>.
              Voters with an audit PDF can confirm their receipt is genuine without trusting us.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ href, title, body, icon: Icon }: { href: string; title: string; body: string; icon: () => React.ReactElement }) {
  return (
    <Link to={href} className="group block bg-white border border-ink-200 rounded-xl p-5 hover:border-ink-300 hover:shadow-soft transition">
      <div className="w-10 h-10 rounded-lg bg-ink-100 text-ink-700 grid place-items-center mb-4 group-hover:bg-brand-50 group-hover:text-brand-600 transition">
        <Icon />
      </div>
      <div className="font-semibold text-ink-900">{title}</div>
      <div className="text-sm text-ink-600 mt-1">{body}</div>
    </Link>
  );
}

const ip = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
function VoteIcon() { return <svg {...ip}><polyline points="20 6 9 17 4 12" /></svg>; }
function UsersIcon() { return <svg {...ip}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>; }
function HomeIcon() { return <svg {...ip}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>; }
function CalendarIcon() { return <svg {...ip}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>; }
function FileIcon() { return <svg {...ip}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>; }
function SettingsIcon() { return <svg {...ip}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>; }
